/* operationalMomentum.js — Shared operational vocabulary.
 *
 *   ⚠  CANONICAL. Single source of operator language for ChainFlow.
 *      Doctrine: /app/memory/OPERATIONAL_VOCABULARY_DOCTRINE.md
 *
 *   Before inventing a new phrase on a page component, ADD IT HERE
 *   and import it. ChainFlow is not a CRM — operational language is
 *   infrastructure, not a helper utility.
 *
 * Powers the four calm surfaces:
 *   • People (ContactsPage)
 *   • Mission Control
 *   • Sales Progression
 *   • Deal Room
 *
 * Doctrine
 *   - Resolve > Display · Reduce > Add · Calm > Stimulate
 *   - Every string here is signal-rich (something just happened or is
 *     awaited). No generic "Updated today" / "Viewed updates" filler.
 *   - Vocabulary is operational, not CRM / SaaS / productivity.
 *
 * Rules (mirror of the doctrine document):
 *   DO     · Import from this module · Reuse shared stage buckets and
 *            tones · Reuse `composeOperationalContext` · Reuse
 *            `momentumForStage` / `momentumForRole` / `nextStepForStage`
 *            · Extend the existing pools when new wording is needed.
 *   DO NOT · Hardcode ad-hoc transaction statuses · Invent page-specific
 *            wording · Duplicate operational terminology · Create
 *            conflicting stage language · Add noisy or emotionally
 *            charged phrasing · Introduce CRM-style status clutter.
 *
 * Anything that needs to render a stage label, stage tint, role-aware
 * "quiet" placeholder, or activity-feed momentum line MUST import from
 * here. Adding a new operator phrase happens here first.
 */

// ─── Stage taxonomy ────────────────────────────────────────────────
// Collapse the 12-stage canonical pipeline into 5 operator-facing
// buckets — the calm vocabulary every surface speaks:
//   Under Offer · AML · Conveyancing · Pre-Exchange · Exchange
// (Completed lives as a terminal sub-state of Exchange.)
export const STAGE_BUCKET = {
  offer:           "Under Offer",
  memo:            "Under Offer",
  aml:             "AML",
  solicitors:      "Conveyancing",
  mortgage_app:    "Conveyancing",
  survey:          "Conveyancing",
  searches:        "Conveyancing",
  enquiries:       "Conveyancing",
  mortgage_offer:  "Pre-Exchange",
  exchange_ready:  "Pre-Exchange",
  exchanged:       "Exchange",
  completed:       "Exchange",
};

// Restrained tint per stage bucket. Used by People + Mission Control
// + Sales Progression for inline stage chips / progress indicators.
// Never used for fills — only borders, text, dots.
export const STAGE_TONE = {
  "Exchange":     "#047857",
  "Pre-Exchange": "#7C3AED",
  "Conveyancing": "#0E7490",
  "AML":          "#B45309",
  "Under Offer":  "#475569",
};

// ─── Role-aware quiet placeholders ────────────────────────────────
// Surfaced on the People page when a contact has NO linked deals.
// Reads as honest, not lazy — "Standby" tells the operator the
// relationship exists but isn't progressing transactionally today.
// Never invent aggregate counts when no data exists.
export const QUIET_CONTEXT_BY_ROLE = {
  Solicitor:         "Standby — no active matter",
  Buyer:             "Searching — no offer agreed",
  Seller:            "Standby — no buyer matched",
  "Mortgage Broker": "Standby — no application live",
  "Managing Agent":  "Standby — no live request",
  "Estate Agent":    "Standby — no live instruction",
};

// ─── Stage-aware momentum (Mission Control activity sub-lines) ────
// "What just happened" per stage. Rotated deterministically by row
// index so the table reads varied without being generic. Used by
// `contextLine(status, stage, index)` in MissionControlPage.
export const MOMENTUM_BY_STAGE = {
  "Under Offer":   ["Memorandum issued",     "AML kick-off scheduled", "Solicitor instructed"],
  "AML":           ["ID requested",          "AML chase sent",          "Memorandum acknowledged"],
  "Conveyancing":  ["Searches returned",     "Management pack uploaded", "Enquiries raised"],
  "Pre-Exchange":  ["Mortgage offer received", "Buyer solicitor replied", "Enquiries answered"],
  "Exchange":      ["Exchange confirmed",    "Completion date agreed",  "Funds in transit"],
};

// ─── Role-aware momentum (People page Last-active sub-line) ───────
// Rotated per row. Mirrors what an operator would actually see in
// the contact's activity feed — never generic.
export const MOMENTUM_BY_ROLE = {
  Buyer: [
    "Mortgage offer received",
    "Searches returned",
    "Buyer solicitor replied",
    "ID verified",
    "Memorandum acknowledged",
  ],
  Seller: [
    "Management pack uploaded",
    "Title deeds shared",
    "Enquiries replied",
    "Contract pack issued",
    "Leasehold info added",
  ],
  Solicitor: [
    "Enquiries replied",
    "Contract pack issued",
    "Searches received",
    "Mortgage offer acknowledged",
    "TR1 forwarded",
  ],
  "Mortgage Broker": [
    "Mortgage offer issued",
    "AIP renewed",
    "Lender updated",
    "Survey instructed",
    "Offer extended",
  ],
  "Managing Agent": [
    "Pack delivered",
    "Service charge confirmed",
    "Building insurance verified",
    "Ground rent confirmed",
    "Pack requested",
  ],
  "Estate Agent": [
    "Viewing booked",
    "Offer notified",
    "Memo issued",
    "Status updated",
    "Chain refreshed",
  ],
};
export const MOMENTUM_FALLBACK = ["Status updated", "Activity logged", "Note added"];

// ─── Next-Step library (Sales Progression queue) ──────────────────
// Concrete next-action phrases keyed by stage bucket. Surfaced as
// the prominent next-step row label in the operational queue.
export const NEXT_STEP_LIBRARY = {
  "Under Offer":   ["Issue memorandum",                    "Confirm AML pack",            "Brief solicitor"],
  "AML":           ["Awaiting ID · chased",                 "Memo acknowledgement due",     "Solicitor instruction in flight"],
  "Conveyancing":  ["Awaiting management pack · chased",   "Searches commissioned",        "Enquiries raised · solicitor side"],
  "Pre-Exchange":  ["Awaiting mortgage offer · chased",    "Contract pack reviewed",       "Exchange window confirmed"],
  "Exchange":      ["Confirming completion funds",         "Coordinating key release",     "Final pre-completion check"],
};

// ─── Operational signal library ───────────────────────────────────
// Cross-page vocabulary of concrete signals an operator might
// surface on a deal card / activity tile. Kept here as a calm
// reference shared by any future component needing operator
// language without inventing new phrasing.
export const OPERATIONAL_SIGNAL_LIBRARY = [
  "Mortgage offer received",
  "Searches returned",
  "Management pack uploaded",
  "Buyer solicitor replied",
  "Memorandum acknowledged",
  "Enquiries raised",
  "Draft contract received",
  "AML pack verified",
  "Survey booked",
  "TA6 returned",
  "TA10 returned",
  "Exchange confirmed",
  "Completion date agreed",
  "Funds in transit",
  "Buyer ID verified",
  "Title deeds shared",
];

// ─── Last-active rotation ────────────────────────────────────────
// Calm relationship-cadence indicator. Today / yesterday / Nd ago.
export const LAST_ACTIVE_LABELS = ["today", "today", "yesterday", "2 days ago", "today"];

// ─── Helpers ──────────────────────────────────────────────────────

/**
 * Compose Mission Control's "what just happened" sub-line.
 * Determinism keyed by row index so the table reads varied without
 * looking randomised.
 */
export function momentumForStage(stage, index = 0) {
  const pool = MOMENTUM_BY_STAGE[stage] || MOMENTUM_BY_STAGE.Conveyancing;
  return pool[index % pool.length];
}

/**
 * Compose People's PersonRow Last-active detail sub-line.
 */
export function momentumForRole(role, index = 0) {
  const pool = MOMENTUM_BY_ROLE[role] || MOMENTUM_FALLBACK;
  return pool[index % pool.length];
}

/**
 * Sales Progression next-step lookup. Returns a calm sentence
 * appropriate to the current stage bucket.
 */
export function nextStepForStage(stage, index = 0) {
  const pool = NEXT_STEP_LIBRARY[stage] || NEXT_STEP_LIBRARY.Conveyancing;
  return pool[index % pool.length];
}

/**
 * People page Operational Context composer.
 *
 * Input: { contact, transactions } shape. Returns ordered calm
 * aggregate lines (max 3) when transactions exist; falls back to
 * the role-aware honest QUIET_CONTEXT_BY_ROLE phrase otherwise.
 * Never fabricates aggregate counts.
 */
export function composeOperationalContext(row) {
  const txs = row?.transactions || [];
  if (txs.length === 0) {
    return [{
      tone: "muted",
      text: QUIET_CONTEXT_BY_ROLE[row?.contact?.role] || "Standby — no live transactions",
    }];
  }
  const active        = txs.filter((t) => t.status !== "Completed" && t.status !== "Fallen Through");
  const exchangeReady = txs.filter((t) => t.stageLabel === "Pre-Exchange" || t.stageLabel === "Exchange");
  const awaitingMortgage  = txs.filter((t) => t.stage === "mortgage_app" || t.stage === "mortgage_offer");
  const awaitingEnquiries = txs.filter((t) => t.stage === "enquiries");
  const awaitingPack      = row?.contact?.role === "Managing Agent" || row?.contact?.role === "Estate Agent"
    ? txs.filter((t) => t.stage === "enquiries" || t.stage === "searches")
    : [];

  const lines = [];
  if (active.length)            lines.push({ tone: "primary", text: `${active.length} active deal${active.length === 1 ? "" : "s"}` });
  if (exchangeReady.length)     lines.push({ tone: "ready",   text: `${exchangeReady.length} exchange ready` });
  if (awaitingMortgage.length)  lines.push({ tone: "wait",    text: `Awaiting mortgage offer on ${awaitingMortgage.length} deal${awaitingMortgage.length === 1 ? "" : "s"}` });
  if (awaitingEnquiries.length) lines.push({ tone: "wait",    text: `Awaiting enquiries on ${awaitingEnquiries.length} deal${awaitingEnquiries.length === 1 ? "" : "s"}` });
  if (awaitingPack.length)      lines.push({ tone: "warn",    text: `${awaitingPack.length} delayed management pack${awaitingPack.length === 1 ? "" : "s"}` });
  return lines.slice(0, 3);
}

export const TONE_COLOR = {
  primary: "#0F172A",
  ready:   "#047857",
  wait:    "#B45309",
  warn:    "#B91C1C",
  muted:   "#94A3B8",
};


// ═══════════════════════════════════════════════════════════════════
// OPERATIONAL VISIBILITY (Role-sensitive)
// ═══════════════════════════════════════════════════════════════════
//
// ChainFlow surfaces *operational* visibility — what is happening
// to the transaction — never personal presence. There are zero
// "online", "last seen", "viewed", "typing" or "active now"
// indicators. Operational visibility is filtered through one of
// three audience tiers so the same activity stream reads
// appropriately for an internal agent, a buyer/seller client, or
// a third-party solicitor/broker.
//
// Doctrine: /app/memory/OPERATIONAL_VOCABULARY_DOCTRINE.md
//
// • internal     — agents · sales progression · managers · admins
//                  See: full operational updates, milestone
//                  acknowledgements, progression responsiveness,
//                  stale operational states, transaction
//                  inactivity gaps.
// • client       — buyers · sellers
//                  See: softer reassurance language, milestone
//                  movement, transaction momentum.
//                  NEVER see: personal activity timing, internal
//                  responsiveness metrics, solicitor inactivity.
// • third_party  — solicitors · mortgage brokers · managing /
//                  estate agents
//                  See: transaction-related operational events
//                  only (documents uploaded, enquiries received,
//                  contract pack issued).
//                  NEVER see: user-activity presence, "who viewed
//                  what" style behaviour, internal workflow
//                  monitoring.

export const AUDIENCE_INTERNAL     = "internal";
export const AUDIENCE_CLIENT       = "client";
export const AUDIENCE_THIRD_PARTY  = "third_party";

/**
 * Map a user.role string to the appropriate audience tier.
 * Defaults to `third_party` for any role we don't explicitly
 * recognise — strictest fallback by design.
 */
export function audienceForRole(role) {
  const r = (role || "").toLowerCase();
  if (["admin", "director", "manager", "agent", "negotiator", "progressor", "owner"].includes(r)) {
    return AUDIENCE_INTERNAL;
  }
  if (["buyer", "seller", "client"].includes(r)) {
    return AUDIENCE_CLIENT;
  }
  return AUDIENCE_THIRD_PARTY;
}

// ─── Visibility event taxonomy ────────────────────────────────────
// Every operational visibility line carries a `kind` so the audience
// filter can decide what to surface. Kinds are operational verbs —
// never people-events.
export const VISIBILITY_KINDS = {
  document_uploaded:        "document_uploaded",
  document_received:        "document_received",
  milestone_completed:      "milestone_completed",
  milestone_acknowledged:   "milestone_acknowledged",
  enquiries_raised:         "enquiries_raised",
  enquiries_acknowledged:   "enquiries_acknowledged",
  mortgage_offer_received:  "mortgage_offer_received",
  searches_returned:        "searches_returned",
  contract_pack_issued:     "contract_pack_issued",
  management_pack_received: "management_pack_received",
  memorandum_issued:        "memorandum_issued",
  memorandum_acknowledged:  "memorandum_acknowledged",
  responsiveness_lagging:   "responsiveness_lagging",
  inactivity_gap:           "inactivity_gap",
};

// Which kinds are visible to which audience tier. Internal sees
// everything operational. Clients see only momentum (no internal
// responsiveness / inactivity signals). Third-parties see only the
// transactional artefacts they participate in.
export const AUDIENCE_ALLOW_KINDS = {
  [AUDIENCE_INTERNAL]: new Set(Object.values(VISIBILITY_KINDS)),
  [AUDIENCE_CLIENT]: new Set([
    VISIBILITY_KINDS.document_received,
    VISIBILITY_KINDS.milestone_completed,
    VISIBILITY_KINDS.enquiries_raised,
    VISIBILITY_KINDS.enquiries_acknowledged,
    VISIBILITY_KINDS.mortgage_offer_received,
    VISIBILITY_KINDS.searches_returned,
    VISIBILITY_KINDS.management_pack_received,
    VISIBILITY_KINDS.memorandum_issued,
  ]),
  [AUDIENCE_THIRD_PARTY]: new Set([
    VISIBILITY_KINDS.document_uploaded,
    VISIBILITY_KINDS.document_received,
    VISIBILITY_KINDS.enquiries_raised,
    VISIBILITY_KINDS.enquiries_acknowledged,
    VISIBILITY_KINDS.contract_pack_issued,
  ]),
};

// ─── Audience-softened phrasings ─────────────────────────────────
// Each kind has an internal phrasing (precise, operator-facing) +
// a client-soft phrasing (reassurance-focused). Third-parties see
// the internal phrasing for the subset of kinds they're allowed —
// never the responsiveness / inactivity ones (gated above).
export const VISIBILITY_PHRASING = {
  [VISIBILITY_KINDS.document_uploaded]: {
    internal: "Document uploaded",
    client:   "Document received",
  },
  [VISIBILITY_KINDS.document_received]: {
    internal: "Document received",
    client:   "Document received",
  },
  [VISIBILITY_KINDS.milestone_completed]: {
    internal: "Milestone completed",
    client:   "Milestone reached",
  },
  [VISIBILITY_KINDS.milestone_acknowledged]: {
    internal: "Milestone acknowledged",
    client:   "Progress confirmed",
  },
  [VISIBILITY_KINDS.enquiries_raised]: {
    internal: "Enquiries raised",
    client:   "Enquiries underway",
  },
  [VISIBILITY_KINDS.enquiries_acknowledged]: {
    internal: "Enquiries acknowledged",
    client:   "Enquiries progressing",
  },
  [VISIBILITY_KINDS.mortgage_offer_received]: {
    internal: "Mortgage offer received",
    client:   "Mortgage progressing",
  },
  [VISIBILITY_KINDS.searches_returned]: {
    internal: "Searches returned",
    client:   "Searches underway",
  },
  [VISIBILITY_KINDS.contract_pack_issued]: {
    internal: "Contract pack issued",
    client:   "Draft contract received",
  },
  [VISIBILITY_KINDS.management_pack_received]: {
    internal: "Management pack received",
    client:   "Management pack received",
  },
  [VISIBILITY_KINDS.memorandum_issued]: {
    internal: "Memorandum issued",
    client:   "Sale confirmed in writing",
  },
  [VISIBILITY_KINDS.memorandum_acknowledged]: {
    internal: "Memorandum acknowledged",
    client:   "Sale confirmed in writing",
  },
  // INTERNAL-ONLY phrasings — never softened to client wording.
  [VISIBILITY_KINDS.responsiveness_lagging]: {
    internal: "Solicitor response time lagging",
    client:   null,
  },
  [VISIBILITY_KINDS.inactivity_gap]: {
    internal: "No operational activity for 3 days",
    client:   null,
  },
};

/**
 * Resolve a visibility line for a viewer audience.
 *
 *   input  · { kind, at?, by?, source? }
 *   output · { kind, text, at } or null if audience cannot see it
 *
 * Returning null is the calm degradation path — caller renders
 * nothing rather than leaking an internal-only signal to a client.
 */
export function lineForAudience(line, audience) {
  if (!line || !line.kind) return null;
  const allowed = AUDIENCE_ALLOW_KINDS[audience] || AUDIENCE_ALLOW_KINDS[AUDIENCE_THIRD_PARTY];
  if (!allowed.has(line.kind)) return null;
  const phrasing = VISIBILITY_PHRASING[line.kind];
  if (!phrasing) return null;
  const text = audience === AUDIENCE_CLIENT ? phrasing.client : phrasing.internal;
  if (!text) return null;
  return { kind: line.kind, text, at: line.at || null, by: line.by || null };
}

/**
 * Filter + soften an array of raw operational visibility lines for
 * the given audience. Empty / null entries are dropped.
 */
export function visibilityLinesForAudience(rawLines, audience) {
  return (rawLines || [])
    .map((l) => lineForAudience(l, audience))
    .filter(Boolean);
}

// ─── Visibility setting defaults ─────────────────────────────────
// Lightweight per-user preferences. Stored in localStorage by the
// `useOperationalVisibility` hook. No complex permission matrix —
// just calm enterprise-appropriate switches.
export const VISIBILITY_SETTINGS_KEY = "chainflow.visibility.v1";

export const VISIBILITY_DEFAULTS = {
  show_operational_updates:    true,
  show_milestone_ack:          true,
  show_document_confirmations: true,
  hide_timestamps:             false,
  internal_only:               false,
};

/**
 * Apply a settings object to an audience-resolved line array.
 * Returns the lines + a `showTime` flag the renderer should read
 * for timestamp visibility.
 */
export function applyVisibilitySettings(lines, settings) {
  const s = { ...VISIBILITY_DEFAULTS, ...(settings || {}) };
  if (!s.show_operational_updates) return { lines: [], showTime: false };
  let out = lines || [];
  if (!s.show_milestone_ack) {
    out = out.filter((l) => l.kind !== VISIBILITY_KINDS.milestone_acknowledged);
  }
  if (!s.show_document_confirmations) {
    out = out.filter((l) => l.kind !== VISIBILITY_KINDS.document_uploaded
                          && l.kind !== VISIBILITY_KINDS.document_received);
  }
  return { lines: out, showTime: !s.hide_timestamps };
}



// ═══════════════════════════════════════════════════════════════════
// REAL ACTIVITY → VISIBILITY MAPPER
// ═══════════════════════════════════════════════════════════════════
//
// Translates a server-side DealActivity row into the shared
// VISIBILITY_KINDS taxonomy so every operational surface (People,
// Mission Control, Deal Room, Sales Progression) can read the same
// real activity stream and surface audience-correct phrasing.
//
// Input shape (DealActivity / chainStore.activity merged shape):
//   { activity_type?: string, kind?: string, title?: string,
//     text?: string, description?: string, created_at?: string,
//     at?: string }
//
// Output: { kind, at } compatible with `lineForAudience` /
// `visibilityLinesForAudience`, or `null` if no operational meaning
// can be derived (the surface should ignore that row).
//
// Doctrine: never invent — if we can't classify it, return null.

const _ACTIVITY_TYPE_TO_KIND = {
  document_upload:        VISIBILITY_KINDS.document_uploaded,
  document_received:      VISIBILITY_KINDS.document_received,
  document_request:       VISIBILITY_KINDS.document_uploaded,
  milestone_completed:    VISIBILITY_KINDS.milestone_completed,
  milestone_acknowledged: VISIBILITY_KINDS.milestone_acknowledged,
  stage_change:           VISIBILITY_KINDS.milestone_completed,
  status_change:          VISIBILITY_KINDS.milestone_completed,
};

// Keyword phrases on activity title / description for activities
// where `activity_type` is too generic (e.g. `system`, `message`,
// `reminder_sent`). Most specific phrasing wins — order matters.
const _ACTIVITY_TEXT_TO_KIND = [
  [/mortgage offer/i,        VISIBILITY_KINDS.mortgage_offer_received],
  [/searches?\b.*(returned|in|received)/i, VISIBILITY_KINDS.searches_returned],
  [/searches?\b/i,           VISIBILITY_KINDS.searches_returned],
  [/contract pack/i,         VISIBILITY_KINDS.contract_pack_issued],
  [/draft contract/i,        VISIBILITY_KINDS.contract_pack_issued],
  [/management pack/i,       VISIBILITY_KINDS.management_pack_received],
  [/enquiries?.*ack(nowledge)?d?/i, VISIBILITY_KINDS.enquiries_acknowledged],
  [/enquiries?/i,            VISIBILITY_KINDS.enquiries_raised],
  [/memorandum.*ack/i,       VISIBILITY_KINDS.memorandum_acknowledged],
  [/memorandum/i,            VISIBILITY_KINDS.memorandum_issued],
  [/exchange.*(complete|confirmed|exchanged)/i, VISIBILITY_KINDS.milestone_completed],
  [/^completion\b|\bcompletion (date|confirmed|reached|complete)/i, VISIBILITY_KINDS.milestone_completed],
];

/**
 * Map a single raw activity record to a visibility line.
 * Returns `null` when no operational meaning is detectable.
 */
export function mapActivityToVisibilityKind(activity) {
  if (!activity) return null;
  const at = activity.at || activity.created_at || activity.createdAt || null;
  const at_type = String(activity.activity_type || "").toLowerCase();
  if (at_type && _ACTIVITY_TYPE_TO_KIND[at_type]) {
    // For document_upload, prefer text-pattern recognition so we
    // can pull through `searches_returned` / `management_pack_received`
    // when the title says so (and fall back to plain document_uploaded).
    if (at_type === "document_upload") {
      const matched = _matchByText(activity);
      if (matched) return { kind: matched, at };
    }
    return { kind: _ACTIVITY_TYPE_TO_KIND[at_type], at };
  }
  const text_kind = _matchByText(activity);
  if (text_kind) return { kind: text_kind, at };
  return null; // Calm degradation — no operational meaning detected.
}

function _matchByText(activity) {
  const haystack = [
    activity.title,
    activity.text,
    activity.description,
  ].filter(Boolean).join(" · ");
  if (!haystack) return null;
  for (const [pat, kind] of _ACTIVITY_TEXT_TO_KIND) {
    if (pat.test(haystack)) return kind;
  }
  return null;
}

/**
 * Map an array of raw activity records to visibility lines.
 * Filters out unclassifiable rows. Most-recent first (callers
 * pre-sort by `created_at desc` — both the v2 API and chainStore
 * `addActivity` already produce that order).
 */
export function visibilityLinesFromActivity(activities, { limit = 5 } = {}) {
  return (activities || [])
    .map(mapActivityToVisibilityKind)
    .filter(Boolean)
    .slice(0, limit);
}
